import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface CurrencyMaskOptions {
  prefix: string;     // e.g. '$ ' o ''
  thousands: string;  // e.g. ',' o '.'
  decimal: string;    // e.g. '.' o ','
}

@Directive({
  selector: 'input[pgsCurrencyMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyMaskDirective),
      multi: true
    }
  ]
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  
  @Input() options: CurrencyMaskOptions = {
    prefix: '',
    thousands: ',',
    decimal: '.'
  };
  
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private disabled = false;
  
  constructor(private el: ElementRef<HTMLInputElement>) {}
  
  // ===== ControlValueAccessor =====
  writeValue(value: number | null): void {
    if (value === null || value === undefined || Number.isNaN(value as any)) {
      this.setValue('');
      return;
    }
    this.setValue(this.format(value));
  }
  
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.el.nativeElement.disabled = isDisabled;
  }
  
  // ===== UX =====
  
  // En foco: mostrar "crudo" (sin separadores de miles, sin prefix)
  @HostListener('focus')
  handleFocus(): void {
    if (this.disabled) return;
    
    const current = this.el.nativeElement.value;
    const num = this.parse(current);
    if (num === null) {
      this.setValue('');
    } else {
      // mostramos con decimal '.' (o tu decimal) pero sin thousands
      this.setValue(this.toRawString(num));
      queueMicrotask(() => {
        const i = this.el.nativeElement.value.length;
        this.el.nativeElement.setSelectionRange(i, i);
      });
    }
  }
  
  // Mientras escribe: actualizamos el modelo sin formatear agresivo
  @HostListener('input')
  handleInput(): void {
    if (this.disabled) return;
    const text = this.el.nativeElement.value;
    
    // Limpieza suave: permite dígitos + un decimal
    const cleaned = this.cleanInput(text);
    if (cleaned !== text) {
      const pos = this.el.nativeElement.selectionStart ?? cleaned.length;
      this.setValue(cleaned);
      // Mejor esfuerzo: restaurar caret
      queueMicrotask(() => this.el.nativeElement.setSelectionRange(pos - 1, pos - 1));
    }
    
    const num = this.parse(cleaned);
    this.onChange(num);
  }
  
  // En blur: formatear con separador de miles + prefix
  @HostListener('blur')
  handleBlur(): void {
    this.onTouched();
    if (this.disabled) return;
    
    const num = this.parse(this.el.nativeElement.value);
    this.onChange(num);
    
    this.setValue(num === null ? '' : this.format(num));
  }
  
  // Bloqueo básico de teclas no permitidas (opcional pero útil)
  @HostListener('keydown', ['$event'])
  handleKeydown(e: KeyboardEvent): void {
    if (this.disabled) return;
    
    const allowed = [
      'Backspace','Delete','Tab','Escape','Enter',
      'Home','End','ArrowLeft','ArrowRight'
    ];
    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
    
    const isDigit = /^[0-9]$/.test(e.key);
    const isDecimal = e.key === this.options.decimal;
    
    // Permitir también '.' aunque tu decimal sea ',' (por comodidad)
    const isDotAsDecimal = this.options.decimal !== '.' && e.key === '.';
    
    if (isDigit || isDecimal || isDotAsDecimal) return;
    
    e.preventDefault();
  }
  
  // ===== Helpers =====
  
  private setValue(v: string): void {
    this.el.nativeElement.value = v ?? '';
  }
  
  private cleanInput(text: string): string {
    const { prefix, decimal } = this.options;
    
    let s = (text ?? '').trim();
    
    // quita prefix si alguien lo pegó
    if (prefix && s.startsWith(prefix)) s = s.slice(prefix.length);
    
    // permitir solo dígitos y separador decimal
    // además permitimos '.' para usuarios aunque decimal sea ','
    const dec = this.escapeRegExp(decimal);
    s = s.replace(new RegExp(`[^0-9${dec}.]`, 'g'), '');
    
    // normalizar '.' a tu decimal si corresponde
    if (decimal !== '.') {
      s = s.replace(/\./g, decimal);
    }
    
    // permitir un solo separador decimal
    const parts = s.split(decimal);
    if (parts.length > 2) {
      s = parts.shift()! + decimal + parts.join('');
    }
    
    return s;
  }
  
  private parse(text: string): number | null {
    const { prefix, thousands, decimal } = this.options;
    if (text == null) return null;
    
    let s = String(text).trim();
    if (!s) return null;
    
    // quitar prefix
    if (prefix && s.startsWith(prefix)) s = s.slice(prefix.length);
    s = s.trim();
    if (!s) return null;
    
    // quitar thousands
    if (thousands) {
      const reThousands = new RegExp(this.escapeRegExp(thousands), 'g');
      s = s.replace(reThousands, '');
    }
    
    // decimal -> '.'
    if (decimal && decimal !== '.') {
      const reDecimal = new RegExp(this.escapeRegExp(decimal), 'g');
      s = s.replace(reDecimal, '.');
    }
    
    // limpiar cualquier resto no válido
    s = s.replace(/[^\d.]/g, '');
    
    // si quedan varios puntos, consolidar
    const parts = s.split('.');
    if (parts.length > 2) {
      s = parts.shift()! + '.' + parts.join('');
    }
    
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  
  private format(value: number): string {
    const { prefix, thousands, decimal } = this.options;
    
    // separar parte entera y decimal (sin redondeo especial)
    const raw = this.toRawString(value); // usa '.' internamente
    const [intPart, decPart] = raw.split('.');
    
    const grouped = this.addThousands(intPart, thousands);
    const withDecimal = decPart !== undefined
      ? grouped + decimal + decPart
      : grouped;
    
    return (prefix ?? '') + withDecimal;
  }
  
  private toRawString(value: number): string {
    // convierte a string evitando notación científica en la mayoría de casos
    // y sin separadores de miles
    // Para tu caso usual (montos normales) alcanza.
    return String(value);
  }
  
  private addThousands(intPart: string, thousands: string): string {
    if (!thousands) return intPart;
    // inserta separador cada 3 dígitos desde la derecha
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  }
  
  private escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
